import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import { AlertStatusSwitch } from 'src/common/components/AlertStatusSwitch/AlertStatusSwitch';
import { Button } from 'common/components/Buttons/Button';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useExternalRowSelectionApi } from 'src/common/components/ConfigurableTable/Components/Rows/ExternalRowSelection/hooks/useExternalRowSelectionApi';
import { useState } from 'react';
import { TariffSummaryAlertConfigurationDialog } from './TarifSummaryAlertConfigurarationDialog';
import { useTariffSummaryQueryData } from 'products/tariffSummary/hooks/useTariffSummaryData.ts';

const Container = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(5),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(10),
}));

const ToggleContainer = styled(Box)(({ theme }) => ({
  '.MuiToggleButtonGroup-root': {
    height: theme.spacing(44),
  },
}));

const AlertInfoContainer = styled(Box)<{ enabled?: boolean }>(({ theme, enabled = false }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(10),
  opacity: enabled ? 1 : 0.4,

  '& > .MuiTypography-root > .MuiTypography-root': {
    fontWeight: enabled ? '600' : 400,
  },
}));

export const TariffSummaryAlertsToolbar = () => {
  const { t } = useTranslation();
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState(false);

  const { selectedIds, unSelectAll } = useExternalRowSelectionApi();

  const tariffSummaryData = useTariffSummaryQueryData();

  const selectedTariffIds = Array.from(selectedIds) as string[];
  const numberOfSelectedRows = selectedTariffIds.length;

  const data = selectedTariffIds.map((id) => {
    if (selectedIds.size === 1 && tariffSummaryData) {
      const bundleCode = selectedIds.values().next().value;

      const selectedTariff = tariffSummaryData.items.find(({ bundleProductCode }) => {
        return bundleProductCode === bundleCode;
      });

      const alertConfiguration = selectedTariff
        ? selectedTariff.alertConfiguration
        : {
            dataThreshold: null,
            voiceThreshold: null,
            smsThreshold: null,
          };

      return {
        bundleProductCode: id,
        alertConfiguration: alertConfiguration,
      };
    }

    return {
      bundleProductCode: id,
      alertConfiguration: {
        dataThreshold: null,
        voiceThreshold: null,
        smsThreshold: null,
      },
    };
  });

  return (
    <>
      <Container>
        <ToggleContainer>
          <AlertStatusSwitch />
        </ToggleContainer>
        <Button
          variant="primary"
          iconStart={<NotificationsOutlinedIcon />}
          onClick={() => setIsAlertDialogOpen(true)}
          disabled={!numberOfSelectedRows}
          size="medium"
          aria-label={t('tariffSummary.table.usageAlert.setUpAlerts')}
          sx={{ height: '2.75rem' }}
        >
          {t('tariffSummary.table.usageAlert.setUpAlerts')}
        </Button>
        <AlertInfoContainer enabled={!!numberOfSelectedRows}>
          <Divider orientation="vertical" sx={{ height: '1rem' }} />
          <Typography variant="body2" data-testid="numberOfSelected">
            <Typography component="span">{numberOfSelectedRows}</Typography> {t('common.selected')}
          </Typography>
        </AlertInfoContainer>
        {!!numberOfSelectedRows && (
          <Button
            variant="tertiary"
            onClick={() => unSelectAll()}
            disabled={!numberOfSelectedRows}
            size="medium"
            aria-label="Clear selection"
            sx={{ height: '2.75rem' }}
          >
            {t('tariffSummary.table.usageAlert.clearSelection')}
          </Button>
        )}
      </Container>
      <TariffSummaryAlertConfigurationDialog
        data={data}
        open={isAlertDialogOpen}
        onClose={() => {
          setIsAlertDialogOpen(false);
        }}
      />
    </>
  );
};
